import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { toJS } from 'mobx';
import { SingleValue } from 'react-select';

import { IStore } from '../../../../services/api/troubleshooting-service';
import CurrentPortalStore from '../../../../stores/current-portal-store';
import { IOption, SelectDropdown } from '../../../../components/inputs/select-dropdown/select-dropdown';
import { Button } from '../../../../components/inputs/button/button';
import TroubleshootingStore from '../../troubleshooting-page-store';

import StoreFeaturesDisplay from './store-features-display/store-features-display';
import './store-features-page.scss';

const StoreFeaturesPage: React.FC = observer(() => {
    const [chosenStore, setChosenStore] = useState<IOption | null>(null);
    const [shopifyResponse, setShopifyResponse] = useState<any>(null);
    const [isActivatingFeature, setIsActivatingFeature] = useState<boolean>(false);
    const [activatingFeatureId, setActivatingFeatureId] = useState<number | null>(null);
    const [isDeactivatingFeature, setIsDeactivatingFeature] = useState<boolean>(false);
    const [deactivatingFeatureId, setDeactivatingFeatureId] = useState<number | null>(null);
    const [isSettingQuota, setIsSettingQuota] = useState<boolean>(false);
    const [settingQuotaFeatureId, setSettingQuotaFeatureId] = useState<number | null>(null);

    useEffect(() => {
        TroubleshootingStore.fetchStores();
        CurrentPortalStore.load();
    }, []);

    const portalId = CurrentPortalStore.currentPortal.id;
    const portalName = CurrentPortalStore.currentPortal.name;
    const portalDisplayName = CurrentPortalStore.currentPortal.displayName;

    useEffect(() => {
        if (chosenStore?.value !== portalId && portalId && portalName) {
            setChosenStore({
                value: portalId,
                label: portalName + (portalDisplayName ? ' (' + portalDisplayName + ')' : '')
            });
        }
    }, [portalId, portalName, portalDisplayName, chosenStore?.value]);

    /** patch to prevent react warning */
    const __shopifyResponse = TroubleshootingStore.shopifyResponse;
    useEffect(() => {
        setShopifyResponse(toJS(TroubleshootingStore.shopifyResponse));
    }, [__shopifyResponse]);

    const generateSelectValues = (): IOption[] | [] => {
        return TroubleshootingStore.stores?.map((el: IStore): IOption => {
            return {
                value: el.id,
                label: el.name + (el.display_name ? ' (' + el.display_name + ')' : '')
            }
        }) || [];
    }

    const onStoreChangeHandler = (val: SingleValue<IOption>) => {
        if (val) {
            setChosenStore(val);
            // Clear results when store changes
            setShopifyResponse(null);
        }
    }

    const performFetch = () => {
        if (chosenStore?.value) {
            TroubleshootingStore.fetchStoreFeatures(chosenStore.value);
        }
    }

    const handleActivateFeature = async (featureId: number) => {
        if (chosenStore?.value) {
            setIsActivatingFeature(true);
            setActivatingFeatureId(featureId);

            try {
                await TroubleshootingStore.activateFeatureForShop(chosenStore.value, featureId);
                // Refresh the features data after activation
                await TroubleshootingStore.fetchStoreFeatures(chosenStore.value);
            } finally {
                setIsActivatingFeature(false);
                setActivatingFeatureId(null);
            }
        }
    };

    const handleDeactivateFeature = async (featureId: number) => {
        if (chosenStore?.value) {
            setIsDeactivatingFeature(true);
            setDeactivatingFeatureId(featureId);

            try {
                await TroubleshootingStore.deactivateFeatureForShop(chosenStore.value, featureId);
                // Refresh the features data after deactivation
                await TroubleshootingStore.fetchStoreFeatures(chosenStore.value);
            } finally {
                setIsDeactivatingFeature(false);
                setDeactivatingFeatureId(null);
            }
        }
    };

    const handleSetQuota = async (featureId: number, quota: number) => {
        if (chosenStore?.value) {
            setIsSettingQuota(true);
            setSettingQuotaFeatureId(featureId);

            try {
                await TroubleshootingStore.setFeatureQuota(chosenStore.value, featureId, quota);
                // Refresh the features data after setting quota
                await TroubleshootingStore.fetchStoreFeatures(chosenStore.value);
            } finally {
                setIsSettingQuota(false);
                setSettingQuotaFeatureId(null);
            }
        }
    };

    const canPerformFetch = !!chosenStore?.value;

    return (
        <div className="store-features-page">
            <div className="card">
                <h4>Request Information</h4>
                <div className="controls">
                    <p className="head5">Store</p>
                    <SelectDropdown
                        required
                        value={chosenStore || undefined}
                        placeholder="Type to find and select store"
                        options={generateSelectValues()}
                        onChange={onStoreChangeHandler}
                    />
                </div>
                <Button
                    disabled={!canPerformFetch}
                    text="Fetch"
                    onClick={performFetch}
                />
            </div>

            {shopifyResponse?.data ? (
                <div className="card results-card">
                    <StoreFeaturesDisplay
                        features={shopifyResponse.data}
                        storeId={chosenStore?.value}
                        onActivateFeature={handleActivateFeature}
                        onDeactivateFeature={handleDeactivateFeature}
                        onSetQuota={handleSetQuota}
                        isActivating={isActivatingFeature}
                        activatingFeatureId={activatingFeatureId}
                        isDeactivating={isDeactivatingFeature}
                        deactivatingFeatureId={deactivatingFeatureId}
                        isSettingQuota={isSettingQuota}
                        settingQuotaFeatureId={settingQuotaFeatureId}
                    />
                </div>
            ) : null}
        </div>
    );
});

export default StoreFeaturesPage;

